import { Injectable } from '@angular/core'
import { Observable, throwError, of } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { HttpClient, HttpEvent, HttpParams, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { LoadboardActivityReportResponse } from '../../app/components/reports/report-progress';

const REPORTS_TIMEOUT_MS = 120_000;
const LOADBOARD_ACTIVITY_POLL_TIMEOUT_MS = 300_000;

@Injectable({
    providedIn: 'root'
})
export class ReportsService {
    constructor(private http: HttpClient) { }

    private downloadBlob(
        url: string,
        params?: HttpParams | Record<string, string>,
        timeoutMs = REPORTS_TIMEOUT_MS,
    ): Observable<HttpEvent<Blob>> {
        return this.http.get(url, {
            params: params || {},
            observe: 'events',
            reportProgress: true,
            responseType: 'blob',
        }).pipe(timeout(timeoutMs));
    }

    getUsersWithPhoneMismatched() {
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/users_with_phone_mismatched`);
    }

    getUsersByLoadBoardActivity() {
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/users_by_loadboard_activity`);
    }

    getLoadsByLoadBoardActivity(
        loadboardId: string,
        dateFrom?: string,
        dateTo?: string,
        refresh = false,
    ): Observable<LoadboardActivityReportResponse> {
        let params = new HttpParams().set('loadboardId', loadboardId);
        if (dateFrom) {
            params = params.set('date_from', dateFrom);
        }
        if (dateTo) {
            params = params.set('date_to', dateTo);
        }
        if (refresh) {
            params = params.set('refresh', 'true');
        }
        return this.http.get(
            `${environment.analyticsAPI}/reports/pg/loads_by_loadboards_activity`,
            { params, responseType: 'text', observe: 'response' },
        ).pipe(
            switchMap((res: HttpResponse<string>) => {
                const body = (res.body || '').trim();
                const contentType = res.headers.get('content-type') || '';
                if (
                    contentType.includes('text/csv')
                    || (body && !body.startsWith('{') && !body.startsWith('['))
                ) {
                    return throwError({
                        name: 'InvalidResponse',
                        message: 'The analytics API returned a file instead of a status response. Please redeploy the latest analytics service.',
                    });
                }
                try {
                    return of(JSON.parse(body) as LoadboardActivityReportResponse);
                } catch (_) {
                    return throwError({
                        name: 'InvalidResponse',
                        message: 'Could not read the report status from the server.',
                    });
                }
            }),
            timeout(LOADBOARD_ACTIVITY_POLL_TIMEOUT_MS),
        );
    }

    getUsersByCompany(fromDate: string) {
        const params = new HttpParams().set('date_from', fromDate);
        return this.downloadBlob(
            `${environment.analyticsAPI}/reports/pg/users_by_companies`,
            fromDate ? params : {},
        );
    }

    getLoadCountByLoadboards(fromDate: string, toDate: string) {
        const params = new HttpParams().set('date_from', fromDate).set('date_to', toDate);
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/loads_counts_by_loadboards`, params);
    }

    getLoadCountByDates(fromDate: string, toDate: string, action: string) {
        const params = new HttpParams()
            .set('date_from', fromDate)
            .set('date_to', toDate)
            .set('filter_by', action);
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/loads_by_dates`, params);
    }

    getListOfCompanies(fromDate: string) {
        const params = new HttpParams().set('date_from', fromDate);
        return this.downloadBlob(
            `${environment.analyticsAPI}/reports/pg/list_of_companies`,
            fromDate ? params : {},
        );
    }

    getLastPostedByCompany() {
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/admins_by_companies`);
    }

    getLastPostedByUser() {
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/admins_users_by_companies`);
    }

    getLoadsByCompanies({ from, to }) {
        const params = new HttpParams().appendAll({
            date_from: from,
            date_to: to,
        });
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/loads_counts_by_companies`, params);
    }

    getNewCompanies(from: string, to: string) {
        const params = new HttpParams().appendAll({
            date_from: from,
            date_to: to,
        });
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/new_companies`, params);
    }

    getLoadboardPostingTimeouts(
        from: string,
        to: string,
        type: string = 'timeout',
        loadboardId?: string,
    ) {
        let params = new HttpParams().appendAll({
            date_from: from,
            date_to: to,
            type,
        });
        if (loadboardId) {
            params = params.append('loadboard_id', loadboardId);
        }
        return this.downloadBlob(`${environment.analyticsAPI}/reports/pg/loadboard_posting_timeouts`, params);
    }

    getTMSIntegration() {
        return this.downloadBlob(
            `${environment.url}/company/export/companies-tms/sa?token=${localStorage.getItem('token')}`,
            {},
        );
    }

    getLoadRejectsReport() {
        return this.downloadBlob(`${environment.url}/shipment/load-rejects-report`);
    }
}
